/**
 * Tenant SLA API Service (GH #2390)
 *
 * The FI admin's own task-queue SLA thresholds: the hours at which a task in
 * the Tasks queue moves from "Within SLA" to "Approaching" and from
 * "Approaching" to "Over SLA". The backend is platform-admin-api at
 * /api/v1/admin/tenants/me/sla (getMyTenantSlaConfig, settings:read;
 * putMyTenantSlaConfig, settings:update). The tenant comes from the caller's
 * session, so no request here names one.
 *
 * Types derived from platform-admin.openapi.yaml -- no manual interfaces. A GET
 * answers TenantSlaConfigResponse, the thresholds plus `platformDefault`, true
 * while the institution has saved none (GH #4317); a PUT sends and answers
 * TenantSlaConfig.
 *
 * Note on baseUrl: the canonical spec authors this path in full, so the client
 * anchors at the origin ('') and calls the generated key as written (the
 * deal-alerts.ts precedent).
 */

import createClient from 'openapi-fetch';
import type { paths as PlatformAdminPaths } from '@aaimplatform/institutional-types/platform-admin';
import type { components } from '@aaimplatform/institutional-types/platform-admin';
import { authMiddleware } from '../client';

export type TenantSlaConfig = components['schemas']['TenantSlaConfig'];
export type TenantSlaConfigResponse = components['schemas']['TenantSlaConfigResponse'];

const tenantSlaClient = createClient<PlatformAdminPaths>({ baseUrl: '', credentials: 'include' });
tenantSlaClient.use(authMiddleware);

/**
 * The response body, or an Error carrying the server's message. A refused
 * save (400) carries the reason the server gave, which the page shows as is.
 */
function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || result.data === undefined) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const body = result.error as { message?: string } | undefined;
    throw new Error(body?.message ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export const tenantSlaApi = {
  get: async (): Promise<TenantSlaConfigResponse> =>
    unwrap(await tenantSlaClient.GET('/api/v1/admin/tenants/me/sla')),

  put: async (body: TenantSlaConfig): Promise<TenantSlaConfig> =>
    unwrap(await tenantSlaClient.PUT('/api/v1/admin/tenants/me/sla', { body })),
};
